/**
 * Keyboard shortcut sheet (`?`) — DESIGN.md §13.3, KEYS-14/SHELL-6.
 *
 * Rendered straight from the live binding table, so a shortcut that exists is
 * documented and one that is documented exists. Opened by the `help` UI event
 * (the `?` key, the palette's "Keyboard shortcuts" command).
 */
import { useEffect, useMemo, useState } from 'react';
import { Keyboard } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatChord, type HotkeyBinding } from '@/lib/hotkeys';
import { onUi } from '@/lib/ui-events';

export function ShortcutHelp({ bindings }: { bindings: HotkeyBinding[] }): JSX.Element {
  const [open, setOpen] = useState(false);

  useEffect(() => onUi('help', () => setOpen(true)), []);

  const groups = useMemo(() => {
    const byGroup = new Map<string, HotkeyBinding[]>();
    for (const binding of bindings) {
      if (binding.hidden) continue;
      const list = byGroup.get(binding.group);
      if (list) list.push(binding);
      else byGroup.set(binding.group, [binding]);
    }
    return [...byGroup.entries()];
  }, [bindings]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[85dvh] max-w-2xl overflow-y-auto max-sm:max-h-dvh">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Keyboard className="size-5 shrink-0" aria-hidden="true" />
            Keyboard shortcuts
          </DialogTitle>
          <DialogDescription>
            One key, no chords. Shortcuts are ignored while you are typing in a field.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-5 sm:grid-cols-2">
          {groups.map(([group, items]) => (
            <section key={group} aria-labelledby={`shortcuts-${slug(group)}`}>
              <h3
                id={`shortcuts-${slug(group)}`}
                className="text-muted-foreground mb-2 text-xs font-semibold uppercase tracking-wide"
              >
                {group}
              </h3>
              <dl className="grid gap-1.5">
                {items.map((binding) => (
                  <div
                    key={`${binding.group}-${binding.key}-${binding.mod ? 'mod' : ''}`}
                    className="flex items-baseline justify-between gap-3"
                  >
                    <dt className="min-w-0 text-sm">{binding.description}</dt>
                    <dd className="shrink-0">
                      <Kbd>{formatChord(binding)}</Kbd>
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function Kbd({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <kbd className="bg-muted text-foreground rounded border px-1.5 py-0.5 font-mono text-xs leading-none">
      {children}
    </kbd>
  );
}

const slug = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]+/g, '-');
